import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadAuftragListStatus: 'idle',
    loadAuftragListError: undefined as any,
    loadAuftragListLastArgs: undefined as any,
    loadAuftragStatus: 'idle',
    loadAuftragError: undefined as any,
    saveAuftragStatus: 'idle',
    saveAuftragError: undefined as any,

    auftragList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadAuftragList = createAsyncThunk('meldungAuftragSlice/loadAuftragList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().meldungAuftragSlice.loadAuftragListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/nurisauftrag/?action=gte&MIN_MELDUNGID=${parentModel.HDR_ID}&MAX_MELDUNGID=${parentModel.HDR_ID}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadAuftragListStatus } = getState().meldungAuftragSlice
        return loadAuftragListStatus !== 'pending'
    }
})

export const loadAuftrag = createAsyncThunk('meldungAuftragSlice/loadAuftrag', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/nurisauftrag/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadAuftragStatus } = getState().meldungAuftragSlice
        return loadAuftragStatus !== 'pending'
    }
})

export const saveAuftrag = createAsyncThunk('meldungAuftragSlice/saveAuftrag', async ({ auftrag }: any, { rejectWithValue }: any) => {
    let promises = []
    if (auftrag?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/nurisauftrag/${ auftrag?.HDR_ID }/`,
            prepareData(auftrag),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/nurisauftrag/`,
            prepareData(auftrag),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveAuftragStatus } = getState().meldungAuftragSlice
        return saveAuftragStatus !== 'pending'
    }
})

const AuftragSlice = createSlice({
    name: 'meldungAuftragSlice',
    initialState,
    reducers: {
        resetloadAuftragListError: (state) => {
            state.loadAuftragListError = undefined
        },
        resetloadAuftrag: (state) => {
            state.formItem = undefined
            state.loadAuftragError = undefined
        },
        resetloadAuftragError: (state) => {
            state.loadAuftragError = undefined
        },
        resetsaveAuftrag: (state) => {
            state.formItem = undefined
            state.saveAuftragError = undefined
        },
        resetsaveAuftragError: (state) => {
            state.saveAuftragError = undefined
        },
        removeFromAuftragListById: (state, action) => {
            state.auftragList = state.auftragList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromAuftragListByIds: (state, action) => {
            state.auftragList = state.auftragList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.auftragList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadAuftragListStatus = 'preloaded'
            state.loadAuftragListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.auftragList = []
            state.loadAuftragListStatus = 'idle'
            state.loadAuftragListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadAuftragList.pending, (state) => {
            state.loadAuftragListStatus = 'pending'
            state.loadAuftragListError = undefined
        })
        builder.addCase(loadAuftragList.fulfilled, (state, action) => {
            state.loadAuftragListStatus = 'idle'

            if (!action.payload) { return }

            state.loadAuftragListLastArgs = action.payload.args

            {
                state.auftragList = action.payload.data

            }
        })
        builder.addCase(loadAuftragList.rejected, (state, action) => {
            state.loadAuftragListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadAuftragListError = action.error.message
            } else {
                state.loadAuftragListError = action.payload
            }
        })

        builder.addCase(loadAuftrag.pending, (state) => {
            state.loadAuftragStatus = 'pending'
            state.loadAuftragError = undefined
        })
        builder.addCase(loadAuftrag.fulfilled, (state, action) => {
            state.loadAuftragStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.auftragList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.auftragList.push(newItem) }
                else { state.auftragList[i] = { ...state.auftragList[i], ...newItem } }
            }
        })
        builder.addCase(loadAuftrag.rejected, (state, action) => {
            state.loadAuftragStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadAuftragError = action.error.message
            } else {
                state.loadAuftragError = action.payload
            }
        })

        builder.addCase(saveAuftrag.pending, (state) => {
            state.saveAuftragStatus = 'pending'
            state.saveAuftragError = undefined
        })
        builder.addCase(saveAuftrag.fulfilled, (state, action) => {
            state.saveAuftragStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.auftragList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.auftragList.push(newItem) }
                else { state.auftragList[i] = { ...state.auftragList[i], ...newItem } }
            }
        })
        builder.addCase(saveAuftrag.rejected, (state, action) => {
            state.saveAuftragStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveAuftragError = action.error.message
            } else {
                state.saveAuftragError = action.payload
            }
        })

    }
})

export const {
    resetloadAuftragListError,
    resetloadAuftrag,
    resetloadAuftragError,
    resetsaveAuftrag,
    resetsaveAuftragError,
    removeFromAuftragListById,
    removeFromAuftragListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = AuftragSlice.actions
export default AuftragSlice.reducer
